import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { RefreshCw, Download, CheckCircle2, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const Check = ({ ok }) =>
  ok ? (
    <CheckCircle2 className="h-4 w-4 text-green-600 inline" aria-label="pass" />
  ) : (
    <XCircle className="h-4 w-4 text-red-500 inline" aria-label="fail" />
  );

const SummaryChip = ({ label, value, danger, testId }) => (
  <div
    className={`rounded-md border px-3 py-2 text-center ${
      danger ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
    }`}
    data-testid={testId}
  >
    <p className={`text-lg font-bold ${danger ? 'text-red-600' : 'text-gray-900'}`}>{value}</p>
    <p className="text-[11px] text-gray-500 leading-tight">{label}</p>
  </div>
);

export const ConfigAuditModal = ({ open, onOpenChange, team, teamLabel }) => {
  const [loading, setLoading] = useState(false);
  const [audit, setAudit] = useState(null);

  const fetchAudit = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/auto-assignment/config-audit?team=${team}`);
      setAudit(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load config audit');
    } finally {
      setLoading(false);
    }
  }, [team]);

  useEffect(() => {
    if (open) fetchAudit();
  }, [open, fetchAudit]);

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(audit, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `config-audit-${audit?.team || team}-${new Date().toISOString().slice(0, 19)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const s = audit?.summary;
  const scopeText = (v) => (v === 'ALL' ? 'All' : Array.isArray(v) ? (v.length ? v.join(', ') : 'All') : String(v));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[85vh] overflow-y-auto" data-testid="config-audit-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between pr-8">
            <span>Auto-Assign Config Audit — {audit?.team_label || teamLabel}</span>
            <span className="flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchAudit} disabled={loading} data-testid="config-audit-refresh">
                <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={downloadJson} disabled={!audit} data-testid="config-audit-download">
                <Download className="h-4 w-4 mr-1" /> JSON
              </Button>
            </span>
          </DialogTitle>
          <DialogDescription>
            Read-only. Mirrors the engine's eligibility gates and checks each processor's scope
            against the oldest {s?.pending_orders_sampled ?? '—'} pending orders
            (of {s?.pending_orders_total ?? '—'} total).
          </DialogDescription>
        </DialogHeader>

        {loading && !audit ? (
          <div className="flex justify-center py-10">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
          </div>
        ) : audit ? (
          <div className="space-y-4">
            <div className="grid grid-cols-4 md:grid-cols-8 gap-2" data-testid="config-audit-summary">
              <SummaryChip label="Total processors" value={s.total_processors} testId="config-audit-summary-total" />
              <SummaryChip label="Auto-assign enabled" value={s.auto_assign_enabled} danger={s.auto_assign_enabled === 0} testId="config-audit-summary-enabled" />
              <SummaryChip label="Active" value={s.effectively_active} danger={s.effectively_active === 0} testId="config-audit-summary-active" />
              <SummaryChip label="Missing states" value={s.missing_states} danger={s.missing_states > 0} testId="config-audit-summary-missing-states" />
              <SummaryChip label="Over capacity" value={s.over_capacity} danger={s.over_capacity > 0} testId="config-audit-summary-over-capacity" />
              <SummaryChip label="Stuck" value={s.stuck} danger={s.stuck > 0} testId="config-audit-summary-stuck" />
              <SummaryChip label="Scope mismatch" value={s.scope_mismatch} danger={s.scope_mismatch > 0} testId="config-audit-summary-scope-mismatch" />
              <SummaryChip label="Engine-usable" value={s.engine_usable} danger={s.engine_usable === 0} testId="config-audit-summary-engine-usable" />
            </div>

            {s.sample_states?.length > 0 && (
              <p className="text-xs text-gray-500" data-testid="config-audit-sample-states">
                Pending queue states in sample: <span className="font-medium">{s.sample_states.join(', ')}</span>
              </p>
            )}

            <div className="overflow-x-auto border rounded-md">
              <table className="w-full text-xs" data-testid="config-audit-table">
                <thead className="tf-thead">
                  <tr>
                    <th className="px-2 py-2 text-left">Processor</th>
                    <th className="px-2 py-2">Enabled</th>
                    <th className="px-2 py-2">Active</th>
                    <th className="px-2 py-2">States</th>
                    <th className="px-2 py-2">Client scope</th>
                    <th className="px-2 py-2">Product scope</th>
                    <th className="px-2 py-2">WIP</th>
                    <th className="px-2 py-2">Stuck</th>
                    <th className="px-2 py-2">Engine pool</th>
                    <th className="px-2 py-2">Covers pending</th>
                    <th className="px-2 py-2 text-left">Exclusion reason</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.processors.map((p) => (
                    <tr key={p.id} className="border-t hover:bg-gray-50" data-testid={`config-audit-row-${p.id}`}>
                      <td className="px-2 py-1.5 font-medium text-gray-900 whitespace-nowrap">{p.name}</td>
                      <td className="px-2 py-1.5 text-center"><Check ok={p.checks.auto_assign_enabled} /></td>
                      <td className="px-2 py-1.5 text-center">
                        <Check ok={p.checks.effectively_active} />
                        <span className="ml-1 text-gray-400">{p.work_status}</span>
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <Check ok={p.checks.has_eligible_states} />
                        <span className="ml-1 text-gray-500">
                          {p.eligible_states.length > 0 ? p.eligible_states.join(', ') : 'none'}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-center text-gray-500 max-w-[120px] truncate" title={scopeText(p.client_scope)}>
                        {scopeText(p.client_scope)}
                      </td>
                      <td className="px-2 py-1.5 text-center text-gray-500 max-w-[160px] truncate" title={scopeText(p.product_scope)}>
                        {scopeText(p.product_scope)}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <Check ok={p.checks.has_capacity} />
                        <span className="ml-1 text-gray-500">{p.wip.active}/{p.wip.limit}</span>
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <Check ok={p.checks.not_stuck} />
                        {p.stuck_count > 0 && <span className="ml-1 text-gray-500">({p.stuck_count})</span>}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        {p.checks.in_engine_pool ? (
                          <Badge className="bg-green-100 text-green-700 hover:bg-green-100">In pool</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Excluded</Badge>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <Check ok={p.checks.covers_pending_orders} />
                        <span className="ml-1 text-gray-500">
                          {p.coverage.orders_matched}/{p.coverage.sample_size}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-left text-red-600 max-w-[220px]">
                        {p.exclusion_reason || <span className="text-green-600">— fully usable</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default ConfigAuditModal;
